/* eslint-disable @typescript-eslint/no-empty-function */
import React, { ReactElement, useRef, useState, MouseEvent, useEffect } from 'react';
import { Typography } from '../Typography/Typography';
import { useClickOutside } from '../../hooks/useClickOutside';
import styles from './Select.module.scss';
import classes from 'classnames/bind';
import Dropdown from '../Dropdown/Dropdown';
import { Control } from 'react-hook-form';
import { ArrowIcon } from '../../assets/icons/ArrowIcon';
import { ArrowDownFilled } from '../../assets/icons/ArrowDownFilled';
import { getScrollableParent } from './utilities';
import Spinner from '../Spinner/Spinner';

const cx = classes.bind(styles);

export interface ISelectProps {
  label: string;
  options: TOption[];
  error?: string;
  control?: Control;
  onChange: (event: CustomEvent<ICustomParams>) => void;
  value: string | number | undefined;
  loading?: boolean;
  maxHeight?: number;
}

export interface ICustomParams {
  target: {
    value: string;
  };
}

export type TOption = {
  value: string;
  name: string | JSX.Element;
};

const Select = ({ label, options, onChange, value, error, loading, maxHeight = 250 }: ISelectProps): ReactElement => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [left, setLeft] = useState<number>(0);
  const [top, setTop] = useState<number>(0);
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);
  const domNode = useClickOutside<HTMLDivElement>(() => setOpenMenu(false));
  const nodeRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  const setDropdownValues = () => {
    if (ref?.current) {
      const { top, left, width } = ref.current.getBoundingClientRect();
      setLeft(left);
      setTop(top);
      setDropdownWidth(width);
    }
  };

  useEffect(() => {
    setDropdownValues();
  }, [ref, top, left, dropdownWidth, openMenu]);

  useEffect(() => {
    window.addEventListener('resize', setDropdownValues);

    return () => {
      window.removeEventListener('resize', setDropdownValues);
    };
  }, []);

  useEffect(() => {
    if (ref?.current) {
      const scrollableParent = getScrollableParent(ref.current);

      scrollableParent.addEventListener('scroll', setDropdownValues);

      return () => {
        scrollableParent.removeEventListener('scroll', setDropdownValues);
      };
    }
  }, []);

  const newOptions = options.map((option) => {
    const event = new CustomEvent('', { detail: { target: { value: 'test' } } });
    const target = { value: option.value };

    Object.defineProperty(event, 'target', { writable: false, value: target });
    return {
      ...option,
      id: option.value,
      onOptionClick: () => onChange(event),
    };
  });

  const handleMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    !loading && setOpenMenu((prevState: boolean) => !prevState);
  };

  const selectedOption = options.find((option) => option?.value === value)?.name;
  return (
    <div className={styles['container']} ref={ref}>
      <div onClick={handleMenu} role="button" aria-label="select" ref={domNode} className={cx('select-wrapper')}>
        <div className={cx('select', { 'error-border': error })}>
          <Typography color="primary">{selectedOption}</Typography>
          {openMenu ? <ArrowIcon /> : <ArrowDownFilled />}
        </div>
        {loading && (
          <div className={cx('spinner')}>
            <Spinner disabled={true} />
          </div>
        )}
        <label
          className={cx('label', {
            'label-selected': selectedOption,
            'label-on-loading': loading,
            'error-color': error,
          })}
        >
          {label}
        </label>
        {error && (
          <Typography size="M" className={cx('error-msg', 'error-color')}>
            {error}
          </Typography>
        )}
      </div>
      {newOptions.length > 0 && (
        <div
          style={{
            top,
            left,
            width: dropdownWidth,
          }}
          className={styles['dropdown-container']}
        >
          <Dropdown options={newOptions} ref={nodeRef} isOpen={openMenu} position="top" maxHeight={maxHeight} />
        </div>
      )}
    </div>
  );
};

export default Select;
